import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import UploadIcon from '@mui/icons-material/Upload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckIcon from '@mui/icons-material/Check';
import { useAuth } from '../../contexts/AuthContext';
import { api, ApiError } from '../../services/api';
import type { AgencyBranding } from '@traaker/shared';

const MAX_LOGO_BYTES = 2 * 1024 * 1024;
const ACCEPTED_TYPES = 'image/png,image/jpeg,image/webp,image/svg+xml';

/**
 * White-label settings form. Surfaces once `reportsEnabled` is on — drives the
 * cover page + footer of every PDF/PPTX report an agency sends to its clients.
 */
export default function AgencyBrandingSettings() {
  const { tenant, refreshProfile } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [agencyName, setAgencyName] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#00DDB3');
  const [footerText, setFooterText] = useState('');
  const [logoPreviewSrc, setLogoPreviewSrc] = useState<string | null>(null);

  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const b = tenant?.settings?.agencyBranding;
    setAgencyName(b?.agencyName || tenant?.name || '');
    setPrimaryColor(b?.primaryColor || '#00DDB3');
    setFooterText(b?.footerText || '');
    if (b?.logoUrl) {
      // The URL is `/api/tenants/agency-branding/logo?t=...`. Fetch as blob so
      // the AuthGuard sees our bearer token (the <img> tag would send the
      // browser's default cookies instead).
      void loadLogoBlob(setLogoPreviewSrc);
    } else {
      setLogoPreviewSrc(null);
    }
  }, [tenant]);

  const handleFileChange = async (file: File | null) => {
    if (!file) return;
    setError(null);
    if (file.size > MAX_LOGO_BYTES) {
      setError('Le logo doit faire moins de 2 Mo.');
      return;
    }
    setUploading(true);
    try {
      const dataUrl = await fileToDataUrl(file);
      const res = await api.post<{ branding: AgencyBranding }>(
        '/tenants/agency-branding/logo',
        { dataUrl },
      );
      await refreshProfile();
      await loadLogoBlob(setLogoPreviewSrc);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      // Keep the color/footer inputs in sync in case the server normalized them.
      if (res.branding.agencyName) setAgencyName(res.branding.agencyName);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Upload du logo impossible.');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setError(null);
    setSaving(true);
    try {
      await api.patch<{ branding: AgencyBranding }>('/tenants/agency-branding', {
        agencyName: agencyName.trim(),
        primaryColor: primaryColor.trim() || undefined,
        footerText: footerText.trim() || '',
      });
      await refreshProfile();
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Erreur lors de l\'enregistrement.');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveLogo = async () => {
    setError(null);
    try {
      await api.delete('/tenants/agency-branding/logo');
      await refreshProfile();
      setLogoPreviewSrc(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Impossible de retirer le logo.');
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Card>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>
            Marque blanche — Rapports
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Ces éléments apparaissent sur tous les rapports PDF / PPTX que vous
            envoyez à vos clients.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {saved && (
            <Alert icon={<CheckIcon fontSize="small" />} severity="success" sx={{ mb: 2 }}>
              Enregistré.
            </Alert>
          )}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems="center" sx={{ mb: 3 }}>
            <Avatar
              src={logoPreviewSrc || undefined}
              variant="rounded"
              sx={{
                width: 96,
                height: 96,
                bgcolor: 'action.hover',
                border: '1px dashed',
                borderColor: 'divider',
                color: 'text.secondary',
                fontSize: 32,
              }}
            >
              {(agencyName || 'A')[0].toUpperCase()}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" fontWeight={600}>Logo de l'agence</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                PNG, JPEG, WebP ou SVG — max 2 Mo. Idéalement carré.
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={uploading ? <CircularProgress size={14} /> : <UploadIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  {logoPreviewSrc ? 'Remplacer le logo' : 'Téléverser un logo'}
                </Button>
                {logoPreviewSrc && (
                  <Button
                    size="small"
                    color="error"
                    variant="text"
                    startIcon={<DeleteOutlineIcon />}
                    onClick={handleRemoveLogo}
                  >
                    Retirer
                  </Button>
                )}
              </Stack>
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_TYPES}
                style={{ display: 'none' }}
                onChange={(e) => void handleFileChange(e.target.files?.[0] || null)}
              />
            </Box>
          </Stack>

          <Stack spacing={2}>
            <TextField
              label="Nom de l'agence"
              value={agencyName}
              onChange={(e) => setAgencyName(e.target.value)}
              fullWidth
              size="small"
              inputProps={{ maxLength: 100 }}
              helperText="Affiché sur la page de couverture et en pied de rapport."
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Couleur principale"
                type="color"
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
                size="small"
                sx={{ width: 160 }}
              />
              <TextField
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
                size="small"
                sx={{ width: 140 }}
                inputProps={{ maxLength: 9 }}
                helperText="Format hex (ex. #00DDB3)"
              />
            </Stack>

            <TextField
              label="Pied de page"
              value={footerText}
              onChange={(e) => setFooterText(e.target.value)}
              fullWidth
              size="small"
              multiline
              rows={2}
              inputProps={{ maxLength: 300 }}
              helperText="Ex. « Confidentiel — rapport établi par Agence XYZ pour Client ABC. »"
            />
          </Stack>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || !agencyName.trim()}
            >
              {saving ? <CircularProgress size={18} /> : 'Enregistrer'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

async function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

async function loadLogoBlob(setSrc: (src: string | null) => void): Promise<void> {
  try {
    const { getAccessToken } = await import('../../services/auth');
    const token = await getAccessToken();
    // Use VITE_API_URL so the logo fetch hits the same host as every other API
    // call — the API and frontend run on different subdomains in prod/dev.
    const base = (import.meta.env.VITE_API_URL as string | undefined) || '/api';
    const res = await fetch(`${base}/tenants/agency-branding/logo`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      setSrc(null);
      return;
    }
    const blob = await res.blob();
    setSrc(URL.createObjectURL(blob));
  } catch {
    setSrc(null);
  }
}
